import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import {
  X, ArrowRight, ArrowLeft, Sparkles, Gauge, Scale, Lock,
  Calculator as CalcIcon, Shield, CheckCircle, Target,
  ShieldCheck, Save, Send, History, Undo2, MessageCircleQuestion, Workflow
} from 'lucide-react';

// Bump this string when you ship a new "What's New" walkthrough.
// Any user who has not seen this exact version will see the popup once.
export const WHATS_NEW_VERSION = 'v3.0-2026-02-cashflow';
const STORAGE_KEY = `papertrail_whatsnew_${WHATS_NEW_VERSION}_seen`;

const steps = [
  {
    icon: Workflow,
    accent: 'from-amber-500 to-orange-600',
    tag: 'New',
    title: 'Cashflow Game Plan: Verify, Apply, Audit, Undo, and What-If Planning',
    description:
      "Your Cashflow Game Plan is now a full operating system for planning, verifying, applying, reviewing, and testing money moves. The new loop: Plan → Verify → Apply → Audit → Undo → What-If.",
    highlights: [
      'Confirm your real balances are accurate.',
      'Apply approved moves with a before/after diff.',
      'Review what you applied — and undo the latest within 5 minutes.',
      'Ask CFO-style what-if questions on a deterministic planner.',
    ],
  },
  {
    icon: ShieldCheck,
    accent: 'from-emerald-500 to-teal-600',
    tag: 'Verified',
    title: 'Verified Cashflow State',
    description:
      "Confirm your saved Cashflow balances are accurate as of today. The Cashflow card now shows whether a plan is Starter, Demo, Needs Verification, Verified, or Stale — so you always know whether your numbers are decision-ready.",
    highlights: [
      'Confirm balances are accurate as of today.',
      "See \u201CVerified today\u201D or \u201CLast verified X days ago.\u201D",
      'Stale plans prompt you to re-verify.',
      'Starter and demo plans are clearly separated from real saved data.',
    ],
  },
  {
    icon: Save,
    accent: 'from-emerald-500 to-cyan-600',
    tag: 'Verified',
    title: 'Verify on Save',
    description:
      'Save and verify your balances in one deliberate step. Keeps verification intentional and user-controlled.',
    highlights: [
      'New \u201CVerify these balances when I save\u201D checkbox.',
      'Hidden for starter and sample/demo plans.',
      'Keeps verification intentional and user-controlled.',
    ],
  },
  {
    icon: Send,
    accent: 'from-blue-500 to-indigo-600',
    tag: 'Apply',
    title: 'Apply These Moves',
    description:
      'Verified users can now apply selected Cashflow Game Plan moves directly from the dashboard. You see a before/after diff before anything is committed, and both your PaperTrail actual balances and saved scenario update together.',
    highlights: [
      'Only available when the plan is verified.',
      'Applies selected transfers, bill payments, and debt payments.',
      'Shows a before/after diff before committing.',
      'Updates both PaperTrail actual balances and the saved Cashflow scenario.',
      'Deposits and skipped payments are informational only and are not auto-applied.',
    ],
  },
  {
    icon: History,
    accent: 'from-slate-500 to-slate-700',
    tag: 'Audit',
    title: 'Applied Moves History',
    description:
      'Review a clean, read-only history of cashflow moves you\u2019ve applied. See dates, totals, action details, and before/after balances at a glance.',
    highlights: [
      'View recent applied moves in a collapsed timeline.',
      'See date/time, action count, total moved, and action details.',
      'Review before/after balances for Personal, Business, and Savings.',
      'History is read-only and scoped to your user account.',
    ],
  },
  {
    icon: Undo2,
    accent: 'from-amber-400 to-rose-500',
    tag: 'Undo',
    title: 'Undo Last Apply',
    description:
      'Made a mistake? You can undo the most recent apply within a 5-minute safety window. The undo reverses both your PaperTrail actual balances and your saved scenario, but the audit row stays so you keep a complete trail.',
    highlights: [
      'Undo is available only for the latest eligible apply.',
      'Undo is time-limited to 5 minutes.',
      'Undo reverses both PaperTrail actual balances and saved scenario balances.',
      "Undone rows stay in the audit trail with an \u201CUndone\u201D status.",
      'Older rows remain read-only.',
    ],
  },
  {
    icon: MessageCircleQuestion,
    accent: 'from-fuchsia-500 to-purple-600',
    tag: 'What-If',
    title: 'CFO What-If Q&A',
    description:
      "Ask cashflow scenario questions in a read-only CFO-style chat. Every answer is anchored to a deterministic planner run \u2014 the AI explains, but never invents balances, applies moves, or writes anything to your account.",
    highlights: [
      "Ask things like \u201CWhat if my bonus is lower?\u201D or \u201CCan I pay Amex in full?\u201D",
      'Suggested prompts get you started in one click.',
      'Deterministic scenario calculations first, AI explanation second.',
      'Chat cannot apply moves, undo moves, or write to balances.',
      'Each answer includes assumptions, deterministic changes, warnings, and a recommended next action.',
    ],
  },
  {
    icon: Sparkles,
    accent: 'from-blue-500 to-indigo-600',
    tag: "What's New",
    title: 'Welcome to the new PaperTrail',
    description:
      "We've shipped a major upgrade focused on helping you hit your numbers every single week. Take 30 seconds to see what's new.",
    highlights: [
      'Smarter dashboard with health meters',
      'Dual-mode rebalancing assistant',
      'Private balance tracking',
      'Required weekly income calculator',
    ],
  },
  {
    icon: Target,
    accent: 'from-emerald-500 to-teal-600',
    tag: 'New',
    title: 'Required Weekly Income',
    description:
      'Your Dashboard now shows exactly how much you need to deposit each week to stay on pace with your plan — based on your allocations, floors and business caps.',
    highlights: [
      'Auto-calculated from your plan',
      'Respects income floors & business caps',
      'Updates live as your plan changes',
    ],
  },
  {
    icon: Gauge,
    accent: 'from-orange-500 to-rose-600',
    tag: 'New',
    title: 'Account Health Meters',
    description:
      'See at a glance which accounts are over- or under-funded. No more guessing where your money should go next.',
    highlights: [
      'Overfunded vs. underfunded indicators',
      'Color-coded for fast scanning',
      'Built into the Dashboard',
    ],
  },
  {
    icon: Scale,
    accent: 'from-purple-500 to-fuchsia-600',
    tag: 'Upgraded',
    title: 'Smart Rebalance — Dual Mode',
    description:
      'Fix underfunded accounts with one click. Choose Instant Rebalance to move money now, or Weekly Catch-Up to spread it across future checks.',
    highlights: [
      'Priority routing: Taxes → Personal → Business → Savings',
      'Minimum balance protections built in',
      'Income-aware catch-up schedule',
    ],
  },
  {
    icon: Lock,
    accent: 'from-slate-600 to-gray-800',
    tag: 'Private',
    title: 'My Balances (private to you)',
    description:
      'Enter your real bank balances in Settings to compare expected vs. actual. Only you can see these — your upline and teammates cannot.',
    highlights: [
      'Expected vs. actual side-by-side',
      'Never visible to leaders or team',
      'Powers the rebalance suggestions',
    ],
  },
  {
    icon: CalcIcon,
    accent: 'from-cyan-500 to-blue-600',
    tag: 'New',
    title: 'Post-Check Summary & Plan Guardrails',
    description:
      'When you log a weekly income, you now see projected balances immediately. Plus, Income Floors and Business Caps keep your plan realistic.',
    highlights: [
      'Instant post-check projections',
      'Income Floor ensures minimum take-home',
      'Business Cap prevents cash hoarding',
    ],
  },
];

export function hasSeenWhatsNew(user) {
  // Server-recorded version takes precedence (cross-device).
  if (user && user.last_seen_whats_new_version === WHATS_NEW_VERSION) return true;
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function markWhatsNewSeen() {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[WhatsNewModal] localStorage set failed (private mode?):', err);
    }
  }
  // Best-effort sync to backend so other devices don't re-show it.
  axios
    .post(`${API}/user/mark-whats-new-seen`, { version: WHATS_NEW_VERSION })
    .catch(() => {
      /* offline or unauthenticated — localStorage still protects this device */
    });
}

export default function WhatsNewModal({ open, onClose }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  if (!open) return null;

  const isLast = step === steps.length - 1;
  const s = steps[step];
  const Icon = s.icon;

  const close = () => {
    markWhatsNewSeen();
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      data-testid="whats-new-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
    >
      <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-800">
        {/* Header accent bar */}
        <div className={`h-2 w-full bg-gradient-to-r ${s.accent}`} />

        {/* Close */}
        <button
          onClick={close}
          aria-label="Close"
          className="absolute right-3 top-3 p-2 rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          data-testid="whats-new-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-4">
            <span
              className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full text-white bg-gradient-to-r ${s.accent}`}
            >
              <Shield className="w-3 h-3" />
              {s.tag}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {step + 1} of {steps.length}
            </span>
          </div>

          <div className="flex items-start gap-4 mb-4">
            <div
              className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${s.accent} flex items-center justify-center shadow-md`}
            >
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                id="whats-new-title"
                className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-tight"
              >
                {s.title}
              </h2>
            </div>
          </div>

          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-5">
            {s.description}
          </p>

          <ul className="space-y-2 mb-6">
            {s.highlights.map((h) => (
              <li key={h} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-200">{h}</span>
              </li>
            ))}
          </ul>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mb-5" data-testid="whats-new-progress">
            {steps.map((stepDef, i) => (
              <button
                key={stepDef.title}
                onClick={() => setStep(i)}
                aria-label={`Go to step ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === step
                    ? 'w-6 bg-blue-600 dark:bg-blue-400'
                    : 'w-1.5 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400'
                }`}
                data-testid={`whats-new-dot-${i}`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={close}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors"
              data-testid="whats-new-skip"
            >
              Skip
            </button>

            <div className="flex items-center gap-2">
              {step > 0 && (
                <button
                  onClick={() => setStep((v) => Math.max(0, v - 1))}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  data-testid="whats-new-back"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
              )}

              {!isLast ? (
                <button
                  onClick={() => setStep((v) => Math.min(steps.length - 1, v + 1))}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm"
                  data-testid="whats-new-next"
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={close}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 transition-colors shadow-sm"
                  data-testid="whats-new-done"
                >
                  Got it
                  <CheckCircle className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
